import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast, Toaster } from "sonner";

const TABS = [
  { key: "settings",     label: "Site Settings" },
  { key: "sales",        label: "Sales" },
  { key: "consignors",   label: "Consignors" },
  { key: "sires",        label: "Bloodlines" },
  { key: "noteworthy",   label: "Past Bulls" },
  { key: "donors",       label: "Donor Cows" },
  { key: "videos",       label: "Videos" },
  { key: "testimonials", label: "Testimonials" },
  { key: "buyer",        label: "Buyer Info" },
  { key: "leads",        label: "Catalog Requests" },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("settings");

  const doLogout = () => { logout(); navigate("/admin/login"); };

  return (
    <div data-testid="admin-dashboard" className="admin-shell">
      <Toaster position="top-right" theme="dark" />
      {window.location.hostname.includes("preview.emergentagent.com") && (
        <div
          data-testid="preview-warning-banner"
          className="px-6 py-3 text-center text-sm font-bold"
          style={{ background: "#B3541E", color: "#FFF8EC" }}
        >
          ⚠ This is the PREVIEW copy — data here is separate from the real site. For real catalog
          requests and edits, go to{" "}
          <a href="https://bullsofthebigsky.com/admin" className="underline">bullsofthebigsky.com/admin</a>
        </div>
      )}
      <header className="border-b" style={{ borderColor: "hsl(var(--border))" }}>
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/" className="font-display text-xl">Bulls of the Big Sky</Link>
            <span className="eyebrow-muted text-[10px]">Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-xs hidden sm:block" style={{ color: "var(--text-muted)" }}>{user?.email}</span>
            <button data-testid="admin-logout" onClick={doLogout} className="text-xs eyebrow-muted hover:text-[color:var(--brass)]">Logout</button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-10">
        <div className="flex flex-wrap gap-2 mb-10 border-b" style={{ borderColor: "hsl(var(--border))" }}>
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`admin-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className="px-4 py-3 text-sm transition border-b-2"
              style={{
                borderColor: tab === t.key ? "var(--brass)" : "transparent",
                color: tab === t.key ? "var(--brass)" : "var(--text-secondary)",
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "settings"     && <SettingsTab />}
        {tab === "sales"        && <SalesTab />}
        {tab === "consignors"   && <ConsignorsTab />}
        {tab === "sires"        && <SiresTab />}
        {tab === "noteworthy"   && <NoteworthyTab />}
        {tab === "donors"       && <DonorsTab />}
        {tab === "videos"       && <VideosTab />}
        {tab === "testimonials" && <TestimonialsTab />}
        {tab === "buyer"        && <BuyerInfoTab />}
        {tab === "leads"        && <CatalogRequestsTab />}
      </div>
    </div>
  );
}

/* ---------- helpers ---------- */
function Input({ label, value, onChange, type = "text", ...rest }) {
  return (
    <label className="block">
      <span className="eyebrow-muted text-[10px] block mb-2">{label}</span>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-11 px-3 bg-transparent border outline-none focus:border-[color:var(--brass)]"
        style={{ borderColor: "hsl(var(--border))", color: "var(--text-primary)" }}
        {...rest}
      />
    </label>
  );
}
function Textarea({ label, value, onChange, rows = 4 }) {
  return (
    <label className="block">
      <span className="eyebrow-muted text-[10px] block mb-2">{label}</span>
      <textarea
        rows={rows}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 bg-transparent border outline-none focus:border-[color:var(--brass)]"
        style={{ borderColor: "hsl(var(--border))", color: "var(--text-primary)" }}
      />
    </label>
  );
}

function handleErr(e) {
  toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message || "Error");
}

/* ---------- Settings ---------- */
function SettingsTab() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/site-settings").then((r) => setData(r.data)).catch(handleErr); }, []);
  if (!data) return <Loading />;
  const set = (k) => (v) => setData({ ...data, [k]: v });
  const save = async () => {
    try { await api.put("/admin/site-settings", data); toast.success("Saved"); } catch (e) { handleErr(e); }
  };
  return (
    <div className="max-w-3xl space-y-5">
      <Input label="Hero Title"      value={data.hero_title}     onChange={set("hero_title")} data-testid="setting-hero-title" />
      <Input label="Hero Subtitle"   value={data.hero_subtitle}  onChange={set("hero_subtitle")} />
      <Input label="Hero Tagline"    value={data.hero_tagline}   onChange={set("hero_tagline")} />
      <Input label="Hero Image URL"  value={data.hero_image_url} onChange={set("hero_image_url")} />
      <Textarea label="Brand Intro"  value={data.brand_intro}    onChange={set("brand_intro")} />
      <Input label="Contact Email"   value={data.contact_email}  onChange={set("contact_email")} />
      <Input label="Contact Phone"   value={data.contact_phone}  onChange={set("contact_phone")} />
      <button data-testid="settings-save" onClick={save} className="btn-primary mt-4">Save Settings</button>
    </div>
  );
}

/* ---------- Sales ---------- */
function SalesTab() {
  const [slug, setSlug] = useState("annual");
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/admin/sales/${slug}`).then((r) => setData(r.data)).catch(handleErr);
  }, [slug]);
  if (!data) return <Loading />;
  const set = (k) => (v) => setData({ ...data, [k]: v });
  const save = async () => {
    try {
      const { id: _id, slug: _s, updated_at: _u, ...payload } = data;
      await api.put(`/admin/sales/${slug}`, payload);
      toast.success("Saved");
    } catch (e) { handleErr(e); }
  };
  return (
    <div className="max-w-3xl space-y-5">
      <div className="flex gap-2">
        {["annual", "legacy"].map((s) => (
          <button key={s} data-testid={`sale-tab-${s}`} onClick={() => setSlug(s)}
            className="px-4 py-2 text-xs uppercase tracking-widest border"
            style={{
              borderColor: slug === s ? "var(--brass)" : "hsl(var(--border))",
              color: slug === s ? "var(--brass)" : "var(--text-secondary)",
            }}>
            {s} Sale
          </button>
        ))}
      </div>
      <Input label="Name"           value={data.name}        onChange={set("name")} />
      <Input label="Tagline"        value={data.tagline}     onChange={set("tagline")} />
      <Input label="Date Label"     value={data.date_label}  onChange={set("date_label")} />
      <Input label="Location"       value={data.location}    onChange={set("location")} />
      <Input label="Catalog URL"    value={data.catalog_url} onChange={set("catalog_url")} />
      <Input label="Hero Image URL" value={data.hero_image_url} onChange={set("hero_image_url")} />
      <Textarea label="Description" value={data.description} onChange={set("description")} rows={5} />
      <Input label="Past Sale Archive URL (e.g. DV Auction 2026 lots)" value={data.past_sale_url} onChange={set("past_sale_url")} />
      <Input label="Past Sale Archive Label" value={data.past_sale_label} onChange={set("past_sale_label")} placeholder="e.g. 2026 Annual Sale — Lots & Results on DV Auction" />
      <ScheduleEditor schedule={data.schedule || []} onChange={(s) => setData({ ...data, schedule: s })} />
      <button data-testid="sale-save" onClick={save} className="btn-primary mt-4">Save Sale</button>
    </div>
  );
}

function ScheduleEditor({ schedule, onChange }) {
  const update = (i, k, v) => {
    const next = [...schedule]; next[i] = { ...next[i], [k]: v }; onChange(next);
  };
  const add = () => onChange([...schedule, { time: "", label: "" }]);
  const remove = (i) => onChange(schedule.filter((_, idx) => idx !== i));
  return (
    <div>
      <div className="eyebrow-muted text-[10px] mb-2">Schedule</div>
      <div className="space-y-3">
        {schedule.map((s, i) => (
          <div key={i} className="flex gap-2">
            <input value={s.time}  onChange={(e) => update(i, "time", e.target.value)}  placeholder="Time"
              className="h-11 px-3 bg-transparent border w-1/3 outline-none focus:border-[color:var(--brass)]"
              style={{ borderColor: "hsl(var(--border))", color: "var(--text-primary)" }} />
            <input value={s.label} onChange={(e) => update(i, "label", e.target.value)} placeholder="Label"
              className="h-11 px-3 bg-transparent border flex-1 outline-none focus:border-[color:var(--brass)]"
              style={{ borderColor: "hsl(var(--border))", color: "var(--text-primary)" }} />
            <button onClick={() => remove(i)} className="px-3 text-xs eyebrow-muted">Remove</button>
          </div>
        ))}
      </div>
      <button onClick={add} className="text-xs eyebrow-muted mt-3 hover:text-[color:var(--brass)]">+ Add Step</button>
    </div>
  );
}

/* ---------- Consignors ---------- */
function ConsignorsTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/admin/consignors"); setItems(data); } catch (e) { handleErr(e); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const { id: cid, ...payload } = editing;
      if (cid) await api.put(`/admin/consignors/${cid}`, payload);
      else     await api.post("/admin/consignors", payload);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) { handleErr(e); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this consignor?")) return;
    try { await api.delete(`/admin/consignors/${id}`); toast.success("Deleted"); load(); } catch (e) { handleErr(e); }
  };

  if (editing) {
    const set = (k) => (v) => setEditing({ ...editing, [k]: v });
    const slugify = (s) => (s || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
    return (
      <div className="max-w-3xl space-y-5">
        <div className="eyebrow">{editing.id ? "Edit Consignor" : "New Consignor"}</div>
        <Input label="Name"        value={editing.name}        onChange={(v) => setEditing({ ...editing, name: v, slug: editing.slug || slugify(v) })} />
        <Input label="Slug (URL)"  value={editing.slug}        onChange={(v) => set("slug")(slugify(v))} placeholder="e.g. little-bitterroot-ranch" />
        <Input label="Location"    value={editing.location}    onChange={set("location")} />
        <Input label="Short Tagline" value={editing.short_tagline} onChange={set("short_tagline")} />
        <Input label="Card Image URL"   value={editing.image_url}   onChange={set("image_url")} />
        <Input label="Hero Image URL (detail page)"  value={editing.hero_image_url}   onChange={set("hero_image_url")} />
        <Input label="Website"     value={editing.website}     onChange={set("website")} />
        <Input label="Contact Email" value={editing.contact_email} onChange={set("contact_email")} />
        <Input label="Contact Phone" value={editing.contact_phone} onChange={set("contact_phone")} />
        <Input label="Order"       value={editing.order}       onChange={(v) => set("order")(Number(v) || 0)} type="number" />
        <Textarea label="Short Description (card)" value={editing.description} onChange={set("description")} />
        <Textarea label="Story (detail page)" value={editing.story} onChange={set("story")} rows={6} />
        <div className="flex gap-3">
          <button data-testid="consignor-save" onClick={save} className="btn-primary">Save</button>
          <button onClick={() => setEditing(null)} className="btn-ghost">Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div className="eyebrow">Consignors ({items.length})</div>
        <button data-testid="consignor-new" onClick={() => setEditing({ slug: "", name: "", location: "", short_tagline: "", description: "", story: "", image_url: "", hero_image_url: "", website: "", contact_email: "", contact_phone: "", order: items.length + 1 })} className="btn-primary">+ New</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((c) => (
          <div key={c.id} className="border p-5 flex gap-4" style={{ borderColor: "hsl(var(--border))" }}>
            {c.image_url && <img src={c.image_url} alt="" className="w-20 h-20 object-cover" />}
            <div className="flex-1 min-w-0">
              <div className="font-display text-xl">{c.name}</div>
              <div className="text-xs" style={{ color: "var(--text-muted)" }}>{c.location}</div>
              <div className="flex gap-3 mt-3">
                <button data-testid={`consignor-edit-${c.id}`} onClick={() => setEditing(c)} className="text-xs eyebrow-muted hover:text-[color:var(--brass)]">Edit</button>
                <button onClick={() => del(c.id)} className="text-xs eyebrow-muted hover:text-red-400">Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Videos ---------- */
function VideosTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = async () => { try { const { data } = await api.get("/admin/videos"); setItems(data); } catch (e) { handleErr(e); } };
  useEffect(() => { load(); }, []);
  const save = async () => {
    try {
      const { id: vid, ...payload } = editing;
      if (vid) await api.put(`/admin/videos/${vid}`, payload);
      else     await api.post("/admin/videos", payload);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { handleErr(e); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this video?")) return;
    try { await api.delete(`/admin/videos/${id}`); load(); } catch (e) { handleErr(e); }
  };

  if (editing) {
    const set = (k) => (v) => setEditing({ ...editing, [k]: v });
    return (
      <div className="max-w-3xl space-y-5">
        <div className="eyebrow">{editing.id ? "Edit Video" : "New Video"}</div>
        <Input label="Title"      value={editing.title}      onChange={set("title")} />
        <Input label="YouTube ID or URL" value={editing.youtube_id} onChange={set("youtube_id")} placeholder="e.g. dQw4w9WgXcQ" />
        <Input label="Thumbnail URL (used when no YouTube ID)" value={editing.thumbnail_url} onChange={set("thumbnail_url")} />
        <Input label="Order" value={editing.order} onChange={(v) => set("order")(Number(v) || 0)} type="number" />
        <Textarea label="Description" value={editing.description} onChange={set("description")} />
        <div className="flex gap-3">
          <button data-testid="video-save" onClick={save} className="btn-primary">Save</button>
          <button onClick={() => setEditing(null)} className="btn-ghost">Cancel</button>
        </div>
      </div>
    );
  }
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div className="eyebrow">Videos ({items.length})</div>
        <button data-testid="video-new" onClick={() => setEditing({ title: "", description: "", youtube_id: "", thumbnail_url: "", order: items.length + 1 })} className="btn-primary">+ New</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((v) => (
          <div key={v.id} className="border p-5" style={{ borderColor: "hsl(var(--border))" }}>
            <div className="font-display text-lg">{v.title}</div>
            <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>{v.youtube_id || "(no source)"}</div>
            <div className="flex gap-3 mt-3">
              <button onClick={() => setEditing(v)} className="text-xs eyebrow-muted hover:text-[color:var(--brass)]">Edit</button>
              <button onClick={() => del(v.id)} className="text-xs eyebrow-muted hover:text-red-400">Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Buyer Info ---------- */
function BuyerInfoTab() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/buyer-info").then((r) => setData(r.data)).catch(handleErr); }, []);
  if (!data) return <Loading />;
  const set = (k) => (v) => setData({ ...data, [k]: v });
  const save = async () => {
    try {
      const { id: _id, updated_at: _u, ...payload } = data;
      await api.put("/admin/buyer-info", payload);
      toast.success("Saved");
    } catch (e) { handleErr(e); }
  };
  return (
    <div className="max-w-3xl space-y-5">
      <Textarea label="Hotels & Lodging" value={data.hotels}    onChange={set("hotels")} />
      <Textarea label="Trucking"         value={data.trucking}  onChange={set("trucking")} />
      <Textarea label="Bidding"          value={data.bidding}   onChange={set("bidding")} />
      <Textarea label="Insurance"        value={data.insurance} onChange={set("insurance")} />
      <Textarea label="Sale Schedule"    value={data.schedule}  onChange={set("schedule")} />
      <Textarea label="Contact"          value={data.contact}   onChange={set("contact")} />
      <button data-testid="buyer-save" onClick={save} className="btn-primary mt-4">Save Buyer Info</button>
    </div>
  );
}

function Loading() {
  return <div className="text-sm" style={{ color: "var(--text-muted)" }}>Loading…</div>;
}

/* ---------- Generic CRUD list for catalog content ---------- */
function ContentCrudTab({ label, endpoint, fields, emptyName, blankRow }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try { const { data } = await api.get(`/admin/${endpoint}`); setItems(data); } catch (e) { handleErr(e); }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, []);

  const save = async () => {
    try {
      const { id: rid, ...payload } = editing;
      // coerce bloodline_tags string -> array
      if (typeof payload.bloodline_tags === "string") {
        payload.bloodline_tags = payload.bloodline_tags.split(",").map((s) => s.trim()).filter(Boolean);
      }
      if (rid) await api.put(`/admin/${endpoint}/${rid}`, payload);
      else     await api.post(`/admin/${endpoint}`, payload);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) { handleErr(e); }
  };
  const del = async (id) => {
    if (!window.confirm(`Delete this ${emptyName}?`)) return;
    try { await api.delete(`/admin/${endpoint}/${id}`); load(); } catch (e) { handleErr(e); }
  };

  if (editing) {
    const set = (k) => (v) => setEditing({ ...editing, [k]: v });
    return (
      <div className="max-w-3xl space-y-5">
        <div className="eyebrow">{editing.id ? `Edit ${label}` : `New ${label}`}</div>
        {fields.map((f) => {
          const v = editing[f.key] ?? (f.kind === "boolean" ? false : "");
          if (f.kind === "textarea") {
            return <Textarea key={f.key} label={f.label} value={v} onChange={set(f.key)} rows={f.rows || 4} />;
          }
          if (f.kind === "number") {
            return <Input key={f.key} label={f.label} value={v} onChange={(x) => set(f.key)(Number(x) || 0)} type="number" />;
          }
          if (f.kind === "boolean") {
            return (
              <label key={f.key} className="flex items-center gap-2 text-xs">
                <input type="checkbox" checked={!!v} onChange={(e) => set(f.key)(e.target.checked)} className="h-4 w-4 accent-[#1E4D75]" />
                <span style={{ color: "var(--text-secondary)" }}>{f.label}</span>
              </label>
            );
          }
          if (f.kind === "select") {
            return (
              <label key={f.key} className="block">
                <span className="eyebrow-muted text-[10px] block mb-2">{f.label}</span>
                <select
                  value={v} onChange={(e) => set(f.key)(e.target.value)}
                  className="w-full h-11 px-3 bg-transparent border outline-none focus:border-[color:var(--brass)]"
                  style={{ borderColor: "hsl(var(--border))", color: "var(--text-primary)" }}
                >
                  {(f.options || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </label>
            );
          }
          if (f.kind === "tags") {
            const str = Array.isArray(v) ? v.join(", ") : v;
            return <Input key={f.key} label={`${f.label} (comma separated)`} value={str} onChange={set(f.key)} placeholder="e.g. Marvel, Bismarck" />;
          }
          return <Input key={f.key} label={f.label} value={v} onChange={set(f.key)} placeholder={f.placeholder} />;
        })}
        <div className="flex gap-3">
          <button onClick={save} className="btn-primary">Save</button>
          <button onClick={() => setEditing(null)} className="btn-ghost">Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div className="eyebrow">{label} ({items.length})</div>
        <button onClick={() => setEditing({ ...blankRow, order: items.length + 1 })} className="btn-primary">+ New</button>
      </div>
      {items.length === 0 ? (
        <div className="border p-10 text-center text-sm" style={{ borderColor: "hsl(var(--border))", color: "var(--text-muted)" }}>
          No {emptyName}s yet.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((it) => (
            <div key={it.id} className="border p-5 flex gap-4" style={{ borderColor: "hsl(var(--border))" }}>
              {it.photo_url && <img src={it.photo_url} alt="" className="w-20 h-20 object-cover" />}
              <div className="flex-1 min-w-0">
                <div className="font-display text-lg">{it.name}</div>
                <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
                  {it.breed || ""}{it.year_sold ? ` • ${it.year_sold}` : ""}{it.sold_to ? ` • → ${it.sold_to}` : ""}{it.owner ? ` • ${it.owner}` : ""}
                </div>
                <div className="flex gap-3 mt-3">
                  <button onClick={() => setEditing(it)} className="text-xs eyebrow-muted hover:text-[color:var(--brass)]">Edit</button>
                  <button onClick={() => del(it.id)} className="text-xs eyebrow-muted hover:text-red-400">Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function NoteworthyTab() {
  return (
    <ContentCrudTab
      label="Past Bulls"
      endpoint="noteworthy-bulls"
      emptyName="bull"
      blankRow={{ name: "", registration: "", breed: "", bred_by: "", sold_to: "", year_sold: "", photo_url: "", description: "", bloodline_tags: [], order: 0, featured: true }}
      fields={[
        { key: "name", label: "Bull Name (e.g. LBRS Genesis G69)" },
        { key: "registration", label: "ASA / Registration #" },
        { key: "breed", label: "Breed", kind: "select", options: [
          { value: "", label: "—" },
          { value: "Simmental", label: "Simmental" },
          { value: "SimAngus",  label: "SimAngus" },
          { value: "Angus",     label: "Angus" },
        ]},
        { key: "bred_by", label: "Bred By (consignor)" },
        { key: "sold_to", label: "Sold To" },
        { key: "year_sold", label: "Year Sold" },
        { key: "photo_url", label: "Photo URL" },
        { key: "description", label: "Description", kind: "textarea", rows: 4 },
        { key: "bloodline_tags", label: "Bloodline Tags", kind: "tags" },
        { key: "order", label: "Order", kind: "number" },
        { key: "featured", label: "Show on site", kind: "boolean" },
      ]}
    />
  );
}

function DonorsTab() {
  return (
    <ContentCrudTab
      label="Donor Cows"
      endpoint="donor-cows"
      emptyName="donor cow"
      blankRow={{ name: "", registration: "", breed: "", owner: "", sire: "", dam: "", photo_url: "", description: "", notable_progeny: "", order: 0, featured: true }}
      fields={[
        { key: "name", label: "Cow Name" },
        { key: "registration", label: "ASA / Registration #" },
        { key: "breed", label: "Breed", kind: "select", options: [
          { value: "", label: "—" },
          { value: "Simmental", label: "Simmental" },
          { value: "SimAngus",  label: "SimAngus" },
          { value: "Angus",     label: "Angus" },
        ]},
        { key: "owner", label: "Owned By (consignor)" },
        { key: "sire", label: "Sire" },
        { key: "dam", label: "Dam" },
        { key: "photo_url", label: "Photo URL" },
        { key: "description", label: "Description", kind: "textarea", rows: 4 },
        { key: "notable_progeny", label: "Notable Progeny", kind: "textarea", rows: 3 },
        { key: "order", label: "Order", kind: "number" },
        { key: "featured", label: "Show on site", kind: "boolean" },
      ]}
    />
  );
}

function SiresTab() {
  return (
    <ContentCrudTab
      label="Featured Bloodlines"
      endpoint="featured-sires"
      emptyName="sire"
      blankRow={{ name: "", nickname: "", registration: "", breed: "", owner: "", photo_url: "", description: "", progeny_count_label: "", sale_slug: "annual", order: 0, featured: true }}
      fields={[
        { key: "name", label: "Sire Name (e.g. TJ Marvel 200V)" },
        { key: "nickname", label: "Nickname (e.g. Marvel)" },
        { key: "registration", label: "ASA / Registration #" },
        { key: "breed", label: "Breed", kind: "select", options: [
          { value: "", label: "—" },
          { value: "Simmental", label: "Simmental" },
          { value: "SimAngus",  label: "SimAngus" },
          { value: "Angus",     label: "Angus" },
        ]},
        { key: "owner", label: "Semen / Owner" },
        { key: "photo_url", label: "Photo URL" },
        { key: "description", label: "Description", kind: "textarea", rows: 4 },
        { key: "progeny_count_label", label: "In Sale (e.g. '12 sons in 2027 Annual')" },
        { key: "sale_slug", label: "Tied to Sale", kind: "select", options: [
          { value: "", label: "— None —" },
          { value: "annual", label: "Annual" },
          { value: "legacy", label: "Legacy" },
        ]},
        { key: "order", label: "Order", kind: "number" },
        { key: "featured", label: "Show on site", kind: "boolean" },
      ]}
    />
  );
}

/* ---------- Testimonials ---------- */
function TestimonialsTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/admin/testimonials"); setItems(data); } catch (e) { handleErr(e); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const { id: tid, ...payload } = editing;
      if (tid) await api.put(`/admin/testimonials/${tid}`, payload);
      else     await api.post("/admin/testimonials", payload);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) { handleErr(e); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this testimonial?")) return;
    try { await api.delete(`/admin/testimonials/${id}`); load(); } catch (e) { handleErr(e); }
  };

  if (editing) {
    const set = (k) => (v) => setEditing({ ...editing, [k]: v });
    return (
      <div className="max-w-3xl space-y-5">
        <div className="eyebrow">{editing.id ? "Edit Testimonial" : "New Testimonial"}</div>
        <Textarea label="Quote *" value={editing.quote}     onChange={set("quote")} rows={5} />
        <Input    label="Author"  value={editing.author}    onChange={set("author")} />
        <Input    label="Operation / Ranch" value={editing.operation} onChange={set("operation")} placeholder="e.g. Smith Cattle Co." />
        <Input    label="Location" value={editing.location} onChange={set("location")} placeholder="e.g. Western KY  •  Fescue Country" />
        <Input    label="Photo URL (optional)" value={editing.photo_url} onChange={set("photo_url")} />
        <Input    label="Order" value={editing.order} onChange={(v) => set("order")(Number(v) || 0)} type="number" />
        <label className="flex items-center gap-2 text-xs">
          <input type="checkbox" checked={!!editing.featured} onChange={(e) => set("featured")(e.target.checked)} className="h-4 w-4 accent-[#1E4D75]" data-testid="testimonial-featured" />
          <span style={{ color: "var(--text-secondary)" }}>Show on home page</span>
        </label>
        <div className="flex gap-3">
          <button data-testid="testimonial-save" onClick={save} className="btn-primary">Save</button>
          <button onClick={() => setEditing(null)} className="btn-ghost">Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div className="eyebrow">Testimonials ({items.length})</div>
        <button data-testid="testimonial-new" onClick={() => setEditing({ quote: "", author: "", operation: "", location: "", photo_url: "", order: items.length + 1, featured: true })} className="btn-primary">+ New</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((t) => (
          <div key={t.id} className="border p-5" style={{ borderColor: "hsl(var(--border))" }}>
            <div className="text-sm leading-relaxed" style={{ color: "var(--text-primary)" }}>
              &ldquo;{t.quote.length > 180 ? t.quote.slice(0, 180) + "…" : t.quote}&rdquo;
            </div>
            <div className="mt-3 text-xs" style={{ color: "var(--text-muted)" }}>
              {t.author || "—"} {t.location && `  •  ${t.location}`} {!t.featured && "  •  (hidden)"}
            </div>
            <div className="flex gap-3 mt-3">
              <button onClick={() => setEditing(t)} className="text-xs eyebrow-muted hover:text-[color:var(--brass)]">Edit</button>
              <button onClick={() => del(t.id)} className="text-xs eyebrow-muted hover:text-red-400">Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Catalog Requests (leads) ---------- */
function CatalogRequestsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/catalog-requests");
      setItems(data);
    } catch (e) { handleErr(e); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try { await api.put(`/admin/catalog-requests/${id}`, { status }); load(); } catch (e) { handleErr(e); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this request?")) return;
    try { await api.delete(`/admin/catalog-requests/${id}`); load(); } catch (e) { handleErr(e); }
  };

  const filtered = items.filter((r) => {
    if (filter === "sms") return r.sms_opt_in && r.phone;
    if (filter === "new") return r.status === "new";
    if (filter === "phone") return !!r.phone;
    return true;
  });

  const copyPhones = () => {
    const phones = filtered.filter((r) => r.phone).map((r) => r.phone).join(", ");
    navigator.clipboard.writeText(phones);
    toast.success(`Copied ${filtered.filter((r) => r.phone).length} phone number(s)`);
  };

  const downloadCsv = async () => {
    try {
      const res = await api.get("/admin/catalog-requests/export.csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "catalog-requests.csv"; document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { handleErr(e); }
  };

  if (loading) return <Loading />;

  const fmt = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="eyebrow">Catalog Requests ({items.length})</div>
          <div className="flex gap-1">
            {[
              { k: "all",   l: "All" },
              { k: "new",   l: "New" },
              { k: "phone", l: "Has Phone" },
              { k: "sms",   l: "SMS Opt-In" },
            ].map((f) => (
              <button
                key={f.k}
                data-testid={`leads-filter-${f.k}`}
                onClick={() => setFilter(f.k)}
                className="px-3 py-1.5 text-[10px] uppercase tracking-widest border"
                style={{
                  borderColor: filter === f.k ? "var(--brass)" : "hsl(var(--border))",
                  color: filter === f.k ? "var(--brass)" : "var(--text-secondary)",
                }}
              >{f.l}</button>
            ))}
          </div>
        </div>
        <div className="flex gap-2">
          <button data-testid="leads-copy-phones" onClick={copyPhones} className="btn-ghost" style={{ height: 40, padding: "0 16px" }}>Copy Phones</button>
          <button data-testid="leads-download-csv" onClick={downloadCsv} className="btn-primary" style={{ height: 40, padding: "0 16px" }}>Export CSV</button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="border p-10 text-center text-sm" style={{ borderColor: "hsl(var(--border))", color: "var(--text-muted)" }}>
          No requests yet.
        </div>
      ) : (
        <div className="overflow-x-auto border" style={{ borderColor: "hsl(var(--border))" }}>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b" style={{ borderColor: "hsl(var(--border))", color: "var(--text-muted)" }}>
                {["Date", "Name", "Phone", "Email", "SMS", "Address", "Status", ""].map((h) => (
                  <th key={h} className="text-left py-3 px-4 eyebrow-muted text-[10px]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} data-testid={`lead-row-${r.id}`} className="border-b align-top" style={{ borderColor: "hsl(var(--border))" }}>
                  <td className="py-3 px-4 text-xs" style={{ color: "var(--text-muted)" }}>{fmt(r.created_at)}</td>
                  <td className="py-3 px-4">{r.name}</td>
                  <td className="py-3 px-4">
                    {r.phone ? <a href={`tel:${r.phone}`} className="link-brass">{r.phone}</a> : "—"}
                  </td>
                  <td className="py-3 px-4">
                    {r.email ? <a href={`mailto:${r.email}`} className="link-brass">{r.email}</a> : "—"}
                  </td>
                  <td className="py-3 px-4">
                    {r.sms_opt_in ? <span style={{ color: "var(--brass)" }}>Yes</span> : <span style={{ color: "var(--text-muted)" }}>—</span>}
                  </td>
                  <td className="py-3 px-4 text-xs max-w-xs" style={{ color: "var(--text-secondary)" }}>{r.address || "—"}</td>
                  <td className="py-3 px-4">
                    <select
                      value={r.status || "new"}
                      onChange={(e) => updateStatus(r.id, e.target.value)}
                      className="bg-transparent border text-xs py-1 px-2"
                      style={{ borderColor: "hsl(var(--border))", color: "var(--text-primary)" }}
                    >
                      <option value="new">new</option>
                      <option value="contacted">contacted</option>
                      <option value="mailed">mailed</option>
                      <option value="archived">archived</option>
                    </select>
                  </td>
                  <td className="py-3 px-4">
                    <button onClick={() => del(r.id)} className="text-xs eyebrow-muted hover:text-red-400">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-[11px] mt-4" style={{ color: "var(--text-muted)" }}>
        SMS column shows only requests where the buyer opted in to text updates AND provided a phone number.
        Use the CSV export to load into your SMS platform.
      </p>
    </div>
  );
}
